import React, { useEffect, useRef, useState } from 'react';
import { Icon, Icons } from '@silverfort/sf-components';

const theme = {
    background: 'sf-white',
    backgroundHover: 'sf-gray-50',
    text: 'sf-gray-800',
    placeholder: 'sf-gray-400',
    iconDisabled: 'sf-gray-300',
    icon: 'sf-gray-500'
};
const IpTextField = ({
    className = '',
    id,
    width = '200px',
    placeholder = [192, 168, 0, 1],
    ip = '',
    border,
    onChange,
    autoFocus,
    onAdd
}) => {
    const octat1Ref = useRef();
    const octat2Ref = useRef();
    const octat3Ref = useRef();
    const octat4Ref = useRef();

    const [octatsValues, setOctatsValues] = useState(ip ? ip.split('.') : []);
    const [inFocus, setInFocus] = useState('');
    const [clearHover, setClearHover] = useState(false);

    const skipOctat = (value, octIndex) => {
        return value.length === 3 || (value !== '.' && value.endsWith('.'));
    };

    const changeHandler = (val, octIndex) => {
        const octats = [...octatsValues];
        octats[octIndex] = val;
        const stringIp = octats.join('.');
        onChange(stringIp);
    };

    useEffect(() => {
        if (!ip) {
            setOctatsValues(ip ? ip.split('.') : []);
        }
    }, [ip]);

    useEffect(() => {
        octat1Ref.current.focus();
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Enter') {
                onAdd();
                octat1Ref.current.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onAdd]);

    const handlePaste = (e) => {
        e.preventDefault();
        const paste = (e.clipboardData || window.clipboardData).getData('text');
        const pasteArr = paste.split('.');
        if (pasteArr.length === 4) {
            setOctatsValues(pasteArr);
            onChange(paste);
        }
    };

    return (
        <div
            className={`flex h-8 font-sf gap-0.5 items-center rounded-lg ${
                border ? 'border border-sf-gray-100 focus-within:border-sf-primary-500' : ''
            } p-2 font-sf-primary ${
                !border && `bg-${theme.background} hover:bg-${theme.backgroundHover}`
            } ${className}`}
            style={{ width }}>
            <Icon
                icon={Icons.IP}
                className={`mr-0.5 h-4 w-4
                text-${inFocus ? theme.icon : theme.iconDisabled}`}
            />
            <input
                ref={octat1Ref}
                id={`${id}-searchIp1`}
                autoFocus={autoFocus}
                type="text"
                maxLength={3}
                className={`max-w-7 text-center border-none bg-transparent px-0.5 text-13 outline-none text-${theme.text} placeholder:text-${theme.placeholder}`}
                value={octatsValues[0] || ''}
                placeholder={placeholder[0]}
                onPaste={handlePaste}
                onChange={(e) => {
                    const value = e.target.value;
                    const text = value.replace(/[^0-9]/g, '');
                    setOctatsValues([text, ...octatsValues.slice(1)]);
                    changeHandler(text, 0);
                    if (skipOctat(value)) {
                        octat2Ref.current.focus();
                    }
                }}
                autoComplete="off"
                onFocus={() => {
                    setInFocus('true');
                }}
                onBlur={() => setInFocus('false')}
            />
            <span>.</span>
            <input
                ref={octat2Ref}
                id={`${id}-searchIp2`}
                autoFocus={autoFocus}
                type="text"
                maxLength={3}
                className={`max-w-7 text-center border-none bg-transparent px-0.5 text-13 outline-none text-${theme.text} placeholder:text-${theme.placeholder}`}
                value={octatsValues[1] || ''}
                placeholder={placeholder[1]}
                onChange={(e) => {
                    const value = e.target.value;
                    const text = value.replace(/[^0-9]/g, '');
                    setOctatsValues([octatsValues[0], text, ...octatsValues.slice(2)]);
                    changeHandler(text, 1);
                    if (skipOctat(value)) {
                        octat3Ref.current.focus();
                    }
                }}
                autoComplete="off"
                onFocus={() => {
                    setInFocus('true');
                }}
                onBlur={() => setInFocus('false')}
            />
            <span>.</span>
            <input
                ref={octat3Ref}
                id={`${id}-searchIp3`}
                autoFocus={autoFocus}
                type="text"
                maxLength={3}
                className={`max-w-7 text-center  border-none bg-transparent px-0.5 text-13 outline-none text-${theme.text} placeholder:text-${theme.placeholder}`}
                value={octatsValues[2] || ''}
                placeholder={placeholder[2]}
                onChange={(e) => {
                    const value = e.target.value;
                    const text = e.target.value.replace(/[^0-9]/g, '');
                    setOctatsValues([...octatsValues.slice(0, 2), text, octatsValues[3]]);
                    changeHandler(text, 2);
                    if (skipOctat(value)) {
                        octat4Ref.current.focus();
                    }
                }}
                autoComplete="off"
                onFocus={() => {
                    setInFocus('true');
                }}
                onBlur={() => setInFocus('false')}
            />
            <span>.</span>
            <input
                ref={octat4Ref}
                id={`${id}-searchIp4`}
                autoFocus={autoFocus}
                type="text"
                maxLength={3}
                className={`max-w-7 text-center border-none bg-transparent px-0.5 text-13 outline-none text-${theme.text} placeholder:text-${theme.placeholder}`}
                value={octatsValues[3] || ''}
                placeholder={placeholder[3]}
                onChange={(e) => {
                    const value = e.target.value;
                    const text = value.replace(/[^0-9]/g, '');
                    setOctatsValues([...octatsValues.slice(0, 3), text]);
                    changeHandler(text, 3);
                }}
                autoComplete="off"
                onFocus={() => {
                    setInFocus('true');
                }}
                onBlur={() => setInFocus('false')}
            />
            {!!octatsValues.length && (
                <div className="flex flex-row flex-1 justify-end">
                    <Icon
                        icon={Icons.CircleCancel}
                        className={`h-3 w-3 cursor-pointer text-${clearHover ? 'sf-gray-700' : 'sf-gray-400'}`}
                        onMouseEnter={() => setClearHover(true)}
                        onMouseLeave={() => setClearHover(false)}
                        onClick={() => {
                            setOctatsValues([]);
                            setClearHover(false);
                            onChange('');
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default IpTextField;
